import { useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { QRCodeSVG } from 'qrcode.react';
import html2canvas from 'html2canvas';
import DashboardLayout from '../components/DashboardLayout';

const getCurrentYearWeek = () => {
  const now = new Date();
  const startOfYear = new Date(now.getFullYear(), 0, 1);
  const daysDiff = Math.floor((now - startOfYear) / 86400000);
  const week = Math.ceil((daysDiff + startOfYear.getDay() + 1) / 7);
  return `${now.getFullYear()}-Week${String(Math.max(1, week)).padStart(2, '0')}`;
};

const ContainerLabel = () => {
  const labelRef = useRef(null);
  const [form, setForm] = useState({
    inboundId: '',
    shipmentNumber: '',
    admissionNumber: '',
    
    yearWeek: getCurrentYearWeek(),
  });

  const handleChange = (field) => (event) => {
    setForm((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const labelData = useMemo(() => ({
    shipmentNumber: form.shipmentNumber.trim(),
    admissionNumber: form.admissionNumber.trim(),
    yearWeek: form.yearWeek.trim(),
  }), [form]);

  const qrPayload = useMemo(
    () => `${labelData.shipmentNumber || '-'}-${labelData.admissionNumber || '-'}-${labelData.yearWeek || '-'}`,
    [labelData]
  );

  const handlePrint = () => {
    window.print();
  };

  const handleDownload = async () => {
    if (!labelRef.current) return;

    // Export at 300 DPI equivalent for exact 3in x 2in output (900x600 px)
    const targetWidthPx = 900;
    const targetHeightPx = 600;
    const rect = labelRef.current.getBoundingClientRect();
    const scaleX = targetWidthPx / rect.width;
    const scaleY = targetHeightPx / rect.height;
    const exportScale = Math.min(scaleX, scaleY);

    const canvas = await html2canvas(labelRef.current, {
      backgroundColor: '#ffffff',
      scale: exportScale,
      useCORS: true,
      width: rect.width,
      height: rect.height,
    });

    const url = canvas.toDataURL('image/png');
    const link = document.createElement('a');
    link.href = url;
    link.download = `ftz-container-3x2in-${Date.now()}.png`;
    link.click();
  };

  return (
    <DashboardLayout>
      <style>
        {`
          @media print {
            @page {
              margin: 0;
            }
            html, body {
              margin: 0 !important;
              padding: 0 !important;
            }
            body * {
              visibility: hidden !important;
            }
            .container-label-print-card,
            .container-label-print-card * {
              visibility: visible !important;
            }
            .container-label-form {
              display: none !important;
            }
            .container-label-wrapper {
              padding: 0 !important;
              margin: 0 !important;
            }
            .container-label-paper {
              box-shadow: none !important;
              border: none !important;
              padding: 0 !important;
            }
            .container-label-print-card {
              margin: 0 !important;
              position: absolute !important;
              left: 0 !important;
              top: 0 !important;
            }
          }
        `}
      </style>
      <Box
        className="container-label-wrapper"
        sx={{ p: 1.5, width: '100%', minHeight: '100%', display: 'flex' }}
      >
        <Paper
          className="container-label-paper"
          sx={{
            p: { xs: 1.5, sm: 2 },
            width: '100%',
            maxWidth: '100%',
            minHeight: '100%',
            border: '1px solid #e0e0e0',
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
            FTZ-Container
          </Typography>

          <Stack className="container-label-form" spacing={2} sx={{ mb: 3 }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(220px, 1fr))' },
                gap: 2,
                alignItems: 'flex-start',
              }}
            >
              <TextField
                label="Inbound ID"
                value={form.inboundId}
                onChange={handleChange('inboundId')}
                helperText="For form tracking only (not printed on label)"
                sx={{
                  '& .MuiFormHelperText-root': { minHeight: '1.25em' },
                }}
              />
              <TextField
                label="Shipment Number"
                value={form.shipmentNumber}
                onChange={handleChange('shipmentNumber')}
              />
              <TextField
                label="Admission Number"
                value={form.admissionNumber}
                onChange={handleChange('admissionNumber')}
              />
              <TextField
                label="Year-Week"
                value={form.yearWeek}
                onChange={handleChange('yearWeek')}
                helperText="Auto-filled with current year-week"
                sx={{
                  '& .MuiFormHelperText-root': { minHeight: '1.25em' },
                }}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              <Button variant="contained" onClick={handlePrint}>
                Print 2x3 Label
              </Button>
              <Button variant="outlined" onClick={handleDownload}>
                Download Label
              </Button>
            </Box>
          </Stack>

          <Box
            ref={labelRef}
            className="container-label-print-card"
            sx={{
              width: '3in',
              height: '2in',
              border: '1px solid #000',
              backgroundColor: '#fff',
              overflow: 'hidden',
              fontFamily: '"Segoe UI", Arial, sans-serif',
              boxSizing: 'border-box',
              p: 0,
            }}
          >
            <Box
              sx={{
                width: '100%',
                height: '100%',
                display: 'grid',
                gridTemplateColumns: '50% 50%',
                backgroundColor: '#fff',
                overflow: 'hidden',
                p: 0,
                boxSizing: 'border-box',
              }}
            >
              <Box
                sx={{
                  p: '3mm',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'flex-start',
                  gap: '3.6mm',
                }}
              >
                <Box sx={{ width: '100%' }}>
                  <Typography
                    sx={{
                      fontFamily: 'Arial, Helvetica, sans-serif',
                      fontSize: '0.115in',
                      fontWeight: 700,
                      lineHeight: 1.05,
                      letterSpacing: 0.05,
                      color: '#000',
                      textAlign: 'left',
                      textTransform: 'none',
                    }}
                  >
                    Shipment Number:
                  </Typography>
                  <Typography
                    sx={{
                      mt: '0.35mm',
                      fontFamily: 'Arial, Helvetica, sans-serif',
                      fontSize: '0.142in',
                      fontWeight: 700,
                      lineHeight: 1.05,
                      letterSpacing: 0.05,
                      color: '#000',
                      textAlign: 'left',
                      wordBreak: 'break-word',
                    }}
                  >
                    {labelData.shipmentNumber || '-'}
                  </Typography>
                </Box>

                <Box sx={{ width: '100%' }}>
                  <Typography
                    sx={{
                      fontFamily: 'Arial, Helvetica, sans-serif',
                      fontSize: '0.115in',
                      fontWeight: 700,
                      lineHeight: 1.05,
                      letterSpacing: 0.05,
                      color: '#000',
                      textAlign: 'left',
                    }}
                  >
                    ADMISSION NO:
                  </Typography>
                  <Typography
                    sx={{
                      mt: '0.35mm',
                      fontFamily: 'Arial, Helvetica, sans-serif',
                      fontSize: '0.142in',
                      fontWeight: 700,
                      lineHeight: 1.05,
                      letterSpacing: 0.05,
                      color: '#000',
                      textAlign: 'left',
                      wordBreak: 'break-word',
                    }}
                  >
                    {labelData.admissionNumber || '-'}
                  </Typography>
                </Box>

                <Box sx={{ width: '100%' }}>
                  <Typography
                    sx={{
                      fontFamily: 'Arial, Helvetica, sans-serif',
                      fontSize: '0.115in',
                      fontWeight: 700,
                      lineHeight: 1.05,
                      letterSpacing: 0.05,
                      color: '#000',
                      textAlign: 'left',
                    }}
                  >
                    YEAR_WEEK:
                  </Typography>
                  <Typography
                    sx={{
                      mt: '0.35mm',
                      fontFamily: 'Arial, Helvetica, sans-serif',
                      fontSize: '0.142in',
                      fontWeight: 700,
                      lineHeight: 1.05,
                      letterSpacing: 0.05,
                      color: '#000',
                      textAlign: 'left',
                      wordBreak: 'break-word',
                    }}
                  >
                    {labelData.yearWeek || '-'}
                  </Typography>
                </Box>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: '2.2mm',
                  backgroundColor: '#fff',
                  borderLeft: '1px solid #000',
                  boxSizing: 'border-box',
                }}
              >
                <QRCodeSVG
                  value={qrPayload}
                  size={148}
                  level="M"
                  includeMargin={false}
                  bgColor="#FFFFFF"
                  fgColor="#000000"
                />
              </Box>
            </Box>
          </Box>
        </Paper>
      </Box>
    </DashboardLayout>
  );
};

export default ContainerLabel;
